import { TICK } from '../sim/types';

/**
 * Fixed-timestep loop. The simulation always advances in exact TICK steps, which
 * keeps it repeatable, testable and easy to run at other speeds, while rendering
 * runs as fast as the browser allows.
 */
export class Loop {
  private acc = 0;
  private last = 0;
  private raf = 0;
  private running = false;

  /**
   * Cap on simulation ticks per rendered frame. Without it, one slow frame asks
   * for extra ticks, which makes the next frame slower still, and the game
   * never catches up.
   */
  private static readonly MAX_STEPS = 12;

  constructor(
    private readonly step: () => void,
    private readonly draw: (dt: number) => void,
  ) {}

  /** 0 = paused. 1 = real time. 2/4 = fast forward. */
  speed = 1;

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const frame = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.25, (now - this.last) / 1000);
      this.last = now;
      this.acc += dt * this.speed;
      let steps = 0;
      while (this.acc >= TICK && steps < Loop.MAX_STEPS * Math.max(1, this.speed)) {
        this.acc -= TICK;
        this.step();
        steps++;
      }
      if (this.acc > TICK * 4) this.acc = 0;
      this.draw(dt);
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }
}
